/**
 * Team Movement Summary — surfaces aggregate movement-report data on the
 * HR dashboard "My CapScan" tab. Fetches /api/movement-report/my/list and
 * shows: total reports, average delta, top mover (largest +delta), and
 * a compact list of the 3 most recent.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { TrendingUp, ArrowRight, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function MoveStat({ label, value, sub, color = "#0F1E35" }) {
  return (
    <div className="bg-white rounded-xl border border-navy/10 p-4">
      <p className="font-dm text-navy/40 text-[10px] uppercase tracking-wide font-bold mb-1">{label}</p>
      <p className="font-playfair text-2xl font-bold leading-tight" style={{ color }}>{value}</p>
      {sub && <p className="font-dm text-navy/50 text-[11px] mt-1">{sub}</p>}
    </div>
  );
}

export default function TeamMovementSummary() {
  const [list, setList] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/movement-report/my/list`, { withCredentials: true })
      .then((r) => setList(Array.isArray(r.data) ? r.data : []))
      .catch(() => setList([]));
  }, []);

  if (list === null) return null;          // still loading — silent
  if (list.length === 0) return null;      // hide the card when there is no movement data

  const deltas = list.map((r) => Number(r?.headline?.delta || 0));
  const avg = Math.round(deltas.reduce((a, b) => a + b, 0) / deltas.length);
  const topMover = list.reduce((best, cur) => (Number(cur?.headline?.delta || 0) > Number(best?.headline?.delta || -999) ? cur : best), list[0]);
  const wins = list.filter((r) => Number(r?.headline?.delta || 0) > 0).length;
  const avgColor = avg > 0 ? "#2E7D32" : avg < 0 ? "#C62828" : "#0F1E35";
  const topDelta = Number(topMover?.headline?.delta || 0);

  return (
    <div className="bg-gradient-to-br from-cream to-white rounded-2xl border border-navy/10 p-6 shadow-sm" data-testid="team-movement-summary">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-gold" />
          <h3 className="font-playfair text-navy text-lg font-bold">Team Movement Summary</h3>
        </div>
        <span className="font-dm text-navy/50 text-xs">{list.length} movement report{list.length === 1 ? "" : "s"}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <MoveStat label="Reports generated" value={list.length} />
        <MoveStat label="Average movement" value={`${avg > 0 ? "+" : ""}${avg}%`} sub="Present Fit delta" color={avgColor} />
        <MoveStat label="Improvements" value={wins} sub={`out of ${list.length}`} color="#2E7D32" />
        <MoveStat label="Top mover" value={`${topDelta > 0 ? "+" : ""}${topDelta}%`} sub={(topMover?.employee?.name || "—").split(" ").slice(0, 2).join(" ")} color={topDelta > 0 ? "#2E7D32" : "#0F1E35"} />
      </div>

      <div>
        <p className="font-dm text-navy/40 text-[10px] uppercase tracking-wide font-bold mb-2">Recent reports</p>
        <div className="space-y-2">
          {list.slice(0, 3).map((r) => {
            const d = Number(r?.headline?.delta || 0);
            const colour = d > 0 ? "text-green-700" : d < 0 ? "text-red-700" : "text-navy/60";
            return (
              <Link key={r._id} to={`/capscan/movement-report/${r._id}`} data-testid={`movement-report-row-${r._id}`}
                className="flex items-center justify-between bg-white border border-navy/10 rounded-lg px-4 py-2.5 hover:border-gold transition-colors">
                <div className="min-w-0">
                  <p className="font-dm text-navy text-sm font-semibold truncate">{r.employee?.name || "Employee"}</p>
                  <p className="font-dm text-navy/50 text-xs truncate">{r.job_role || "—"} · {r.headline?.label}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`font-playfair text-lg font-bold ${colour}`}>{d > 0 ? "+" : ""}{d}%</span>
                  <ArrowRight className="w-4 h-4 text-navy/40" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {list.length > 3 && (
        <div className="mt-4 text-center">
          <span className="font-dm text-navy/50 text-xs flex items-center justify-center gap-1.5">
            <Sparkles className="w-3 h-3 text-gold" /> {list.length - 3} more available — open any re-scan to view its movement report
          </span>
        </div>
      )}
    </div>
  );
}
